import React from 'react';
import {
  Badge,
  Card,
  PAGER_BUTTON_CLASS,
  ROW_ACTION_TRIGGER_CLASS,
  TABLE_HEADER_CELL_CLASS,
} from '@safetyhub/ui';
import { InspectionDropdownMenu } from './DropdownMenu';
import { HoverPreview } from './HoverPreview';
import { PortalTooltip } from './PortalTooltip';
import type { SiteAccessMembership } from '../../../utils/siteAccess';
import { canModifyInspectionRow } from '../../../utils/inspectionRowAccess';
import type { ReportGate } from '@safetyhub/shared';
import { inspectionStatusConfig } from '../../../lib/inspections';
import { getInspectionStatusIcon } from '../inspectionStatusDisplay';
import {
  ArrowsUpDownIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CircleIcon,
  ClipboardCheckIcon,
} from '../../icons';

interface InspectionListItem {
  id: string;
  siteName: string;
  siteId: string;
  inspectionDate: string;
  inspectorName: string;
  inspectorId: string;
  photoCount: number;
  findingCount: number;
  status: string;
  contributorCount: number;
  collaborators?: { id: string; name: string }[];
  // 미리보기용
  thumbnailUrl?: string;
  highestSeverity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  /** 삭제 일시 (soft delete) */
  deletedAt?: string | null;
  /** 검토된 사진 수 (완료 가능 여부 판단용) */
  reviewedPhotosCount?: number;
  /** 이전 발견사항 중 조치결과 미입력 수 (완료 가능 여부 판단용) */
  unreviewedPreviousFindingsCount?: number;
  /** 현장에서의 점검 순서 (N차) */
  sequenceNumber?: number;
  /** 우수사례 발견사항 수 */
  bestPracticeCount?: number;
  /** 해당 현장에서 가장 최근 점검 여부 */
  isLatestInspection?: boolean;
  /** 예약 점검 예정일 */
  scheduledAt?: string;
  /**
   * 「보고서 생성」 서버 판정(`resolveReportGate`) — 이 행이 **자기 권한과 AND**해서 쓴다.
   * `undefined`면 폴백(종전 판정). 계약 전문은 `lib/inspections.ts`의 이 필드 JSDoc.
   */
  reportGate?: ReportGate;
}

interface InspectionTableProps {
  /** 점검 목록 */
  inspections: InspectionListItem[];
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 현재 사용자 ID (삭제 권한 체크용) */
  currentUserId?: string;
  /** 현재 사용자가 조직 소유자인지 여부 (삭제 권한 체크용) */
  isOwner?: boolean;
  /** 현재 멤버십 (현장 할당 여부 체크용) */
  // 현장 접근 판정에 쓰는 조각만(`isSiteAssigned`). 옛 `role`은 여기서 읽지 않는다 — 소유자
  // 성질은 `isOwner` 필드가 유일한 원천이다(`utils/siteAccess` doc).
  currentMembership?: SiteAccessMembership | null;
  /** 정렬 필드 */
  sortField?: string;
  /** 정렬 방향 */
  sortDirection?: 'asc' | 'desc';
  /** 검토 요청 중인 점검 ID */
  requestingReviewId?: string | null;
  /** 정렬 변경 핸들러 */
  onSort?: (field: string) => void;
  /** 행 클릭 핸들러 */
  onRowClick?: (inspection: InspectionListItem) => void;
  /** 행 메뉴 액션들 */
  onView?: (id: string) => void;
  onGenerateReport?: (id: string) => void;
  onRequestReview?: (id: string) => void;
  onDelete?: (id: string) => void;
  /** 예약 점검 시작 */
  onStartScheduled?: (id: string) => void;
}

const statusConfig = inspectionStatusConfig;

const defaultStatus = {
  label: '알 수 없음',
  color: 'text-ink-600 bg-surface-soft',
  tone: 'slate' as const,
};

/**
 * 위험도 4단계 → Badge tone. 색 계열은 쓰던 것을 그대로 옮겼다(ink→slate, yellow→amber,
 * red→rose). `Badge`의 orange tone이 바로 이 4단계 때문에 있는 것이라(HIGH와 MEDIUM이 한 색으로
 * 뭉치지 않게) HIGH는 orange 그대로다.
 */
const severityConfig = {
  LOW: { label: '낮음', tone: 'slate' },
  MEDIUM: { label: '보통', tone: 'amber' },
  HIGH: { label: '높음', tone: 'orange' },
  CRITICAL: { label: '심각', tone: 'rose' },
} as const;

export function InspectionTable({
  inspections,
  isLoading,
  currentUserId,
  isOwner = false,
  currentMembership,
  sortField,
  sortDirection,
  requestingReviewId,
  onSort,
  onRowClick,
  onView,
  onGenerateReport,
  onRequestReview,
  onDelete,
}: InspectionTableProps) {

  const renderSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowsUpDownIcon className="h-3.5 w-3.5 ml-1 inline-block text-ink-300" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="h-3.5 w-3.5 ml-1 inline-block text-brand-500" />
    ) : (
      <ChevronDownIcon className="h-3.5 w-3.5 ml-1 inline-block text-brand-500" />
    );
  };

  /**
   * 정렬 가능한 헤더 — 공용 셀 결에 hover만 얹는다.
   *
   * 클릭 대상은 `<th>`가 아니라 안쪽 `<button>`이다: th의 onClick은 키보드로 닿지 않아
   * 마우스 없이는 정렬을 쓸 수 없다. `aria-sort`는 셀이 갖는 속성이라 th에 남는다.
   *
   * 컴포넌트가 아니라 **함수로 호출**하는 이유: 렌더 함수 안에서 정의한 컴포넌트는 매 렌더마다
   * 새 타입이 되어 React가 그 자리를 통째로 remount한다. 그러면 정렬을 누른 직후(상태 변경 →
   * 리렌더) 버튼이 새로 마운트되며 포커스를 잃어, 키보드로는 연달아 정렬할 수 없다.
   */
  const renderSortableHeader = (field: string, label: React.ReactNode) => {
    const active = sortField === field;
    return (
      <th
        aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`${TABLE_HEADER_CELL_CLASS} select-none hover:bg-surface-hover`}
      >
        <button
          type="button"
          onClick={() => onSort?.(field)}
          className="flex w-full items-center text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          {label}
          {renderSortIcon(field)}
        </button>
      </th>
    );
  };

  if (isLoading) {
    return (
      <Card padding="none" radius="lg" className="overflow-hidden">
        <div className="animate-pulse p-8 text-center text-ink-400">
          로딩 중...
        </div>
      </Card>
    );
  }

  if (inspections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-card-lg border border-dashed border-line bg-white px-5 py-16 text-center shadow-card-sm">
        <div
          aria-hidden="true"
          className="mb-[18px] grid h-[84px] w-[84px] place-items-center rounded-[22px] border border-brand-100 bg-gradient-to-br from-brand-50 to-white text-brand-500"
        >
          <ClipboardCheckIcon className="h-10 w-10" />
        </div>
        <p className="mb-1.5 text-[17px] font-bold tracking-[-0.015em] text-ink-700">
          점검 내역이 없습니다
        </p>
        <p className="text-[13.5px] font-medium text-ink-500">새 점검을 시작하세요</p>
      </div>
    );
  }

  return (
    <Card padding="none" radius="lg" className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr>
              {renderSortableHeader('siteName', '점검')}
              {renderSortableHeader('inspectionDate', '점검일')}
              {renderSortableHeader('inspectorName', '점검자')}
              <th className={TABLE_HEADER_CELL_CLASS}>사진</th>
              <th className={TABLE_HEADER_CELL_CLASS}>발견</th>
              {renderSortableHeader('status', '상태')}
              <th className={`${TABLE_HEADER_CELL_CLASS} text-right`}>액션</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft bg-white">
            {inspections.map((inspection) => {
              const status = statusConfig[inspection.status] || defaultStatus;
              const StatusIcon = getInspectionStatusIcon(inspection.status);
              const isDeleted = !!inspection.deletedAt;

              return (
                <tr
                  key={inspection.id}
                  className={`hover:bg-surface-soft cursor-pointer transition-colors ${isDeleted ? 'opacity-60 bg-surface-soft' : ''}`}
                  onClick={() => onRowClick?.(inspection)}
                >
                  {/* 현장 */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      {/* 썸네일 미리보기 */}
                      {inspection.thumbnailUrl ? (
                        <HoverPreview imageUrl={inspection.thumbnailUrl} size="sm">
                          <div className="w-10 h-10 rounded-card-sm bg-ink-100 overflow-hidden flex-shrink-0">
                            <img
                              src={inspection.thumbnailUrl}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </HoverPreview>
                      ) : (
                        <div className="w-10 h-10 rounded-card-sm bg-ink-100 flex items-center justify-center text-ink-400 flex-shrink-0">
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-ink-900">
                          {inspection.siteName} {inspection.sequenceNumber ?? 1}차 점검
                        </p>
                        {inspection.highestSeverity && (
                          <Badge
                            tone={severityConfig[inspection.highestSeverity].tone}
                            shape="rounded"
                            size="sm"
                          >
                            위험도: {severityConfig[inspection.highestSeverity].label}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* 점검일 */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-ink-600">
                    {formatDate(inspection.inspectionDate)}
                  </td>

                  {/* 점검자 */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-ink-600">
                    {(() => {
                      // collaborators에서 점검자(주 담당자) 제외한 추가 참여자 수 계산
                      const additionalContributors = inspection.collaborators?.filter(
                        (c) => c.id !== inspection.inspectorId
                      ) ?? [];
                      const additionalCount = additionalContributors.length;

                      if (additionalCount === 0) {
                        return inspection.inspectorName;
                      }

                      // 전체 참여자 목록 (점검자 포함)
                      const allContributors = [
                        { id: inspection.inspectorId, name: inspection.inspectorName, isInspector: true },
                        ...additionalContributors.map((c) => ({ ...c, isInspector: false })),
                      ];

                      return (
                        <PortalTooltip
                          content={
                            <>
                              <div className="text-ink-400 mb-1">점검 참여자</div>
                              <ul className="space-y-0.5">
                                {allContributors.map((c) => (
                                  <li key={c.id}>
                                    {c.name}
                                    {c.isInspector && (
                                      <span className="text-brand-400 text-xs ml-1">(주 담당자)</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </>
                          }
                        >
                          <span className="cursor-default">
                            {inspection.inspectorName}
                            <span className="text-brand-600 font-medium ml-1">+{additionalCount}</span>
                          </span>
                        </PortalTooltip>
                      );
                    })()}
                  </td>

                  {/* 사진 수 */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-ink-600">
                    {inspection.photoCount}
                  </td>

                  {/* 발견 수 */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`text-sm font-medium ${
                        inspection.findingCount > 0 ? 'text-orange-600' : 'text-ink-400'
                      }`}
                    >
                      {inspection.findingCount}
                    </span>
                    {inspection.bestPracticeCount != null && inspection.bestPracticeCount > 0 && (
                      <span className="text-emerald-600 text-xs ml-1">(모범 {inspection.bestPracticeCount})</span>
                    )}
                  </td>

                  {/* 상태 */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {/* SCHEDULED 상태 처리 */}
                      {inspection.status === 'SCHEDULED' ? (
                        <Badge tone="violet">
                          <CircleIcon className="h-3 w-3" />
                          예약됨
                        </Badge>
                      ) : /* IN_PROGRESS 상태의 완료 준비 상태 표시 */
                      inspection.status === 'IN_PROGRESS' && inspection.reviewedPhotosCount !== undefined ? (
                        (() => {
                          const totalPhotos = inspection.photoCount;
                          const reviewedPhotos = inspection.reviewedPhotosCount;
                          const allPhotosReviewed = totalPhotos > 0 && reviewedPhotos === totalPhotos;
                          const canComplete = totalPhotos > 0 && allPhotosReviewed;

                          // 완료 가능하면 초록 tone으로 (알약 배지의 tone 테두리가 함께 붙는다)
                          const statusTone = canComplete ? 'emerald' : status.tone;

                          // 미완료 사유 툴팁 생성
                          const pendingItems: string[] = [];
                          if (totalPhotos === 0) {
                            pendingItems.push('사진 없음');
                          } else if (!allPhotosReviewed) {
                            pendingItems.push(`사진 검토 ${reviewedPhotos}/${totalPhotos}`);
                          }

                          return (
                            <PortalTooltip
                              content={
                                canComplete ? (
                                  <span className="text-green-400">완료 준비됨</span>
                                ) : (
                                  <>
                                    <div className="text-ink-400 mb-1">완료 대기 항목</div>
                                    <ul className="text-xs space-y-0.5">
                                      {pendingItems.map((item, i) => (
                                        <li key={i}>• {item}</li>
                                      ))}
                                    </ul>
                                  </>
                                )
                              }
                            >
                              <Badge tone={statusTone} className="cursor-default">
                                {canComplete ? (
                                  <CheckIcon className="h-3 w-3" />
                                ) : (
                                  <StatusIcon className="h-3 w-3" />
                                )}
                                {canComplete ? '완료 가능' : status.label}
                              </Badge>
                            </PortalTooltip>
                          );
                        })()
                      ) : (
                        <Badge tone={status.tone}>
                          <StatusIcon className="h-3 w-3" />
                          {status.label}
                        </Badge>
                      )}
                      {isDeleted && (
                        <Badge tone="rose" shape="rounded">
                          삭제됨
                        </Badge>
                      )}
                    </div>
                  </td>

                  {/* 액션 - 삭제된 점검은 메뉴 숨김 */}
                  <td className="px-4 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                    {!isDeleted && (() => {
                      // **삭제·검토요청** 권한 체크: 조직 소유자이거나 점검 생성자(주 담당자)이고
                      // 현장 담당자인 경우에만 가능. 식의 소유자는 `utils/inspectionRowAccess`다 —
                      // 스케줄 행 메뉴가 같은 답을 봐야 해서 옮겼다. 여기 손으로 다시 쓰지 말 것.
                      //
                      // ⚠️ **보고서 생성은 2026-10-09부터 이 값을 쓰지 않는다**(`a8061c0c4`) — 서버가
                      // `findAll` 응답의 `reportGate`에 권한 축까지 계산해 싣기 때문이다
                      // (`inspection-report-gate.util.ts` `resolveCanCreateReport` = 집행 식 미러).
                      // 이 값과 AND하면 **집행보다 좁아진다**: 집행의 권한식은
                      // `신원 축(활성 참가자 ∨ 주담당자) ∨ (현장 scope ∧ report.create)`인데 이 함수는
                      // `(소유자 ∥ inspection.manage ∥ 본인 inspectorId) ∧ 현장 배정`이라, **미배정
                      // 활성 참가자**를 막는다. prod 실측으로 그런 **46건**이 거짓 차단되고 있었다
                      // (결정 `2026-10-09-the-enforcement-expression-is-the-canonical-gate.md`).
                      // 🔖 되살리지 말 것 — 되돌림 탐지기는 `lib/__tests__/reportGate.test.ts` ⑬이다.
                      const canModify = canModifyInspectionRow({
                        membership: currentMembership,
                        isOwner: !!isOwner,
                        currentUserId,
                        inspectorId: inspection.inspectorId,
                        siteId: inspection.siteId,
                      });
                      // 이후 차수 점검이 존재하면 삭제 불가
                      const isLockedByLaterInspection = inspection.isLatestInspection === false;
                      return (
                        <InspectionDropdownMenu
                          inspectionId={inspection.id}
                          status={inspection.status}
                          canDelete={canModify}
                          isLockedByLaterInspection={isLockedByLaterInspection}
                          canRequestReview={canModify}
                          // ⚠️⚠️ **이 `true`는 「권한이 없다」가 아니라 「권한 «판정»이 서버로
                          // 옮겨갔다」는 뜻이다 — 권한 검사를 지운 것이 아니다.** 검사는 서버가
                          // 하고(`resolveCanCreateReport` = 집행 식 미러) 그 결과가 아래
                          // `reportGate`에 실려 온다. 즉 축의 소유자가 바뀐 것이고, 여기 `canModify`를
                          // 다시 물리면 **집행보다 좁아진다**(위 `canModify` 주석이 근거의 정본).
                          //
                          // prop 자체를 지우지 않은 이유: `DropdownMenu`의 폴백(`?? isCompleted`)은
                          // **권한을 안 보므로**, 게이트를 싣지 않는 화면이 이 메뉴를 쓰게 되면 그
                          // 폴백이 **권한 없이 문을 여는 경로**가 된다. 그 심층 방어로 남겨 둔다.
                          canGenerateReport
                          // 서버 판정 — 완료 가능성·개선대책 선택·점검완료 서명 **+ 권한**
                          // (2026-10-09부터). 그래서 메뉴는 이 값만 보면 된다.
                          reportGate={inspection.reportGate}
                          isRequestingReview={requestingReviewId === inspection.id}
                          triggerClassName={ROW_ACTION_TRIGGER_CLASS}
                          onView={() => onView?.(inspection.id)}
                          onGenerateReport={() => onGenerateReport?.(inspection.id)}
                          onRequestReview={onRequestReview ? () => onRequestReview(inspection.id) : undefined}
                          onDelete={onDelete ? () => onDelete(inspection.id) : undefined}
                        />
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * 날짜 포맷팅
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * 페이지네이션 컴포넌트
 */
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  const pages = [];
  const maxVisiblePages = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // 페이지 번호 버튼 — 문서관리·결재함 페이저와 같은 문자열이다(목업 `.pager .nav button.cur`).
  const pageButtonClass = (page: number) =>
    `h-8 min-w-[32px] rounded-card-sm border px-2 text-sm font-semibold tabular-nums transition-colors ${
      page === currentPage
        ? 'border-brand-600 bg-brand-600 text-white'
        : 'border-line bg-white text-ink-600 hover:bg-surface-hover hover:text-ink-900'
    }`;

  return (
    <nav className="flex items-center justify-center gap-1 py-4" aria-label="페이지">
      {/* 이전 버튼 */}
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`${PAGER_BUTTON_CLASS} px-2`}
        aria-label="이전 페이지"
      >
        <ChevronLeftIcon className="h-3.5 w-3.5" />
      </button>

      {/* 첫 페이지 */}
      {startPage > 1 && (
        <>
          <button
            type="button"
            onClick={() => onPageChange(1)}
            aria-label="1페이지"
            className={pageButtonClass(1)}
          >
            1
          </button>
          {/* 생략은 버튼이 아니다 — 누를 게 없는데 버튼이면 탭 이동만 늘어난다. */}
          {startPage > 2 && (
            <span aria-hidden="true" className="px-1 text-sm font-semibold text-ink-400">
              …
            </span>
          )}
        </>
      )}

      {/* 페이지 번호들 */}
      {pages.map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => onPageChange(page)}
          aria-current={page === currentPage ? 'page' : undefined}
          aria-label={`${page}페이지`}
          className={pageButtonClass(page)}
        >
          {page}
        </button>
      ))}

      {/* 마지막 페이지 */}
      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && (
            <span aria-hidden="true" className="px-1 text-sm font-semibold text-ink-400">
              …
            </span>
          )}
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            aria-label={`${totalPages}페이지`}
            className={pageButtonClass(totalPages)}
          >
            {totalPages}
          </button>
        </>
      )}

      {/* 다음 버튼 */}
      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`${PAGER_BUTTON_CLASS} px-2`}
        aria-label="다음 페이지"
      >
        <ChevronRightIcon className="h-3.5 w-3.5" />
      </button>
    </nav>
  );
}

export default InspectionTable;
